import * as Sentry from "@sentry/nextjs";
import { isEqual } from "es-toolkit";
import { action, reaction, makeAutoObservable, runInAction, spy } from "mobx";
import * as Y from "yjs";

import { subscribe } from "@/api/cable";
import { MarimoManager } from "@/components/marimo/MarimoManager";
import { SUGGESTIONS_MENU_ATTR } from "@/components/suggestions/constants";
import { nodeIsExpression } from "@/components/tiptap/tiptap-expression/utils";
import { DocumentApiModel } from "@/models/api/DocumentApiModel";
import { ComputedNodeManager } from "@/models/computedNode/ComputedNodeManager";
import { ConnectionStatusModel } from "@/models/ConnectionStatusModel";
import { DEFAULT_CURSOR_CONTEXT_ID } from "@/models/constants";
import { CursorMapModel } from "@/models/CursorMapModel";
import { DocumentPermissionsModel } from "@/models/document/DocumentPermissionsModel";
import { EditorModel } from "@/models/EditorModel";
import { InputModel } from "@/models/InputModel";
import { documentLibrariesStart, LibrariesModel } from "@/models/LibrariesModel";
import { MatrixSelectionModel } from "@/models/MatrixSelectionModel";
import { NodeHoverModel } from "@/models/NodeHoverModel";
import { NodeLookupModel } from "@/models/NodeLookupModel";
import { nodeFromJson } from "@/models/nodes/nodeFromJson";
import { TextNodeModel } from "@/models/nodes/TextNodeModel";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";
import { PlaceholderRegistryModel } from "@/models/PlaceholderRegistryModel";
import { logOrphanNodes } from "@/utils/debug/logOrphanNodes";
import { idGenerator } from "@/utils/idGenerator";
import { createLogger } from "@/utils/logger";
import { migrateDocVersion } from "@/utils/migrateDocVersion";

import type { Document } from "@/api";
import type { AuthModel } from "@/models/auth/AuthModel";
import type { AwarenessModel } from "@/models/AwarenessModel";
import type { CursorContext } from "@/models/CursorMapModel";
import type { CursorModel } from "@/models/CursorModel";
import type { BaseNodeModel } from "@/models/nodes/BaseNodeModel";
import type { ExpressionNodeModel } from "@/models/nodes/ExpressionNodeModel";
import type {
  NodeId,
  SerializedNode,
  NodeModel,
  NodeProps,
  NodePropsValue,
} from "@/models/nodes/types";
import type { SelectionModel } from "@/models/SelectionModel";
import type { Channel } from "@anycable/core";
import type { FlattenedNodeJson } from "@corca/data";
import type { HocuspocusProvider } from "@hocuspocus/provider";
import type { IReactionDisposer } from "mobx";

const logger = createLogger("DocumentModel");

export type YNode = {
  id: NodeId;
  type: string;
  props: Y.Map<NodePropsValue>;
  childrenIds: Y.Array<NodeId>;
};

export type YNodeMap = Y.Map<YNode[keyof YNode]>;

type TransactionOptions = {
  skipYDocUpdate?: boolean;
  /** Wrap transact with MobX runInAction */
  runInAction?: boolean;
  /**
   * Makes the undo manager fold this transaction into the previous stack item instead of
   * starting a new one. Use for follow-up changes that are conceptually part of the action
   * that triggered them (e.g. WrapInGroup wrapping a freshly created expression).
   *
   * Sets a `preventNewUndoStack` flag on the Yjs transaction meta; Collaboration's
   * beforeObserverCalls listener reads it and refreshes the undo manager's lastChange.
   */
  preventNewUndoStack?: boolean;
  /**
   * Forces the undo manager to start a new stack item for this transaction instead of
   * folding it into the previous one (the inverse of `preventNewUndoStack`). Use for
   * discrete user actions that should be independently undoable even when fired in rapid
   * succession within the capture-timeout window (e.g. creating math nodes via suggestions).
   *
   * Sets a `forceNewUndoStack` flag on the Yjs transaction meta; Collaboration's
   * beforeObserverCalls listener reads it and resets the undo manager's lastChange to 0.
   */
  forceNewUndoStack?: boolean;
  skipHistory?: boolean;
  skipComputedRecalc?: boolean;
};

type NodesByType = {
  [T in NodeModel as T["type"]]?: Map<NodeId, T>;
};

// TODO: remove this in favor of models/utils/serializeYNodeMap.ts
export function serializeYNodeMap(yNodeMap: YNodeMap | SerializedNode): SerializedNode {
  if ("toJSON" in yNodeMap) {
    const json = yNodeMap.toJSON();

    if (typeof json.id !== "string") {
      console.error("YNode is missing id", { yNodeMap, json });
    }
    if (typeof json.type !== "string") {
      console.error("YNode is missing type", { yNodeMap, json });
    }
    if (typeof json.props !== "object" || !json.props) {
      console.error("YNode is missing props", { yNodeMap, json });
    }
    if (!Array.isArray(json.childrenIds)) {
      console.error("YNode is missing childrenIds", { yNodeMap, json });
    }
    if (Object.keys(json).length > 4) {
      console.error("YNode has extra keys", { yNodeMap, json });
    }

    return json as SerializedNode;
  }
  return yNodeMap;
}

export class DocumentModel {
  id?: string;

  textNode: TextNodeModel;

  nodes: Map<string, ExpressionNodeModel> = new Map();

  private nodesReactions: Map<string, IReactionDisposer> = new Map();

  yNodesMap: Y.Map<YNodeMap>;

  yDoc: Y.Doc | null;

  yProvider?: HocuspocusProvider;

  /**
   * Connection status to Yjs server
   */
  connectionStatus: ConnectionStatusModel;

  input: InputModel;

  matrixSelection: MatrixSelectionModel;

  nodeHover: NodeHoverModel;

  cursorMap: CursorMapModel;

  placeholderRegistry: PlaceholderRegistryModel;

  editor: EditorModel;

  computedNodes: ComputedNodeManager;

  libraries: LibrariesModel;

  permissions: DocumentPermissionsModel;

  nodesToCleanup: Map<string, ExpressionNodeModel> = new Map();

  nodesToUpdate: Set<string> = new Set();

  nodesToTransform: Set<string> = new Set();

  nodesByType: NodesByType = {};

  codeBlockCount = 0;

  awareness?: AwarenessModel;

  auth?: AuthModel;

  autoFocus = true;

  isFocused = false;

  disableEditing = false;

  onSubmit?: () => void;

  private currentTransactionName: string | null = null;

  private currentTransaction: Y.Transaction | null = null;

  private cableChannel?: Channel;

  private _cursorContextId: string = DEFAULT_CURSOR_CONTEXT_ID;

  /** Custom id generator for child nodes to be used for tests only. You can create a module
   * with a custom generator function and mock it in tests to have different scopes of
   * nanoid mocks.
   * Check apps/web/src/app/components/suggestions/__mocks__/idGenerator.ts and
   * apps/web/src/app/components/suggestions/Suggestions.tsx
   */
  private _idGenerator = idGenerator;

  api: DocumentApiModel;

  private isBatchCreateNodes = false;

  isInitialLoad = false;

  private disposeHandleFocusChange?: () => void;

  private disposeSpy?: () => void;

  nodeLookup: NodeLookupModel | null = null;

  spyStack: string[] = [];

  constructor(
    id?: string,
    yDoc?: Y.Doc | null,
    awareness?: AwarenessModel,
    yProvider?: HocuspocusProvider,
    /** Raw document data from API */
    documentData?: Document,
  ) {
    this.id = id;

    this.placeholderRegistry = new PlaceholderRegistryModel(this);
    this.textNode = this.create(TextNodeModel, undefined, undefined, undefined, "textNode");

    this.yDoc = yDoc === undefined ? new Y.Doc() : yDoc;
    this.yNodesMap = this.yDoc?.getMap("nodes") || new Y.Map();
    this.yProvider = yProvider;
    this.connectionStatus = new ConnectionStatusModel(this.yDoc, this.yProvider);
    this.permissions = new DocumentPermissionsModel(this);

    let nodesJson: SerializedNode[] = [];

    if (this.yDoc) {
      try {
        this.transact(
          () => {
            performance.mark?.("DocumentModel:copyDataFromYDoc:start");
            nodesJson = this.copyDataFromYDoc();
            performance.mark?.("DocumentModel:copyDataFromYDoc:end");
            performance.measure?.(
              "DocumentModel:copyDataFromYDoc",
              "DocumentModel:copyDataFromYDoc:start",
              "DocumentModel:copyDataFromYDoc:end",
            );

            migrateDocVersion(this);
          },
          "DocumentModel:constructor",
          { runInAction: true },
        );

        this.observeYDoc();
      } catch (e) {
        console.error("Error syncing data from yDoc", e);
        Sentry.captureException(e);
        // If there is an error syncing data from yDoc, disable editing to prevent further issues
        this.disableEditing = true;
        // TODO: show error to the user
      }
    }

    this.awareness = awareness;

    this.api = new DocumentApiModel(this, documentData);
    this.input = new InputModel(this);
    this.nodeHover = new NodeHoverModel();
    this.matrixSelection = new MatrixSelectionModel(this);
    this.editor = new EditorModel(this);
    this.computedNodes = new ComputedNodeManager(this);
    this.libraries = new LibrariesModel(undefined, documentLibrariesStart());
    this.cursorMap = new CursorMapModel(this, this.cursorContextId);
    this.editor.hints.initWatchers();

    if (this.yDoc !== null) {
      makeAutoObservable(this, {
        yNodesMap: false,
        transact: false,
        isInitialLoad: false,
        nodesToUpdate: false,
        nodesToTransform: false,
        nodesToCleanup: false,
        spyStack: false,
        onSubmit: false,
      });

      const handleFocusChange = action(() => {
        if (this.editor.tiptap?.isInitialized) {
          const { dom } = this.editor.tiptap.view;
          const { activeElement } = document;

          this.isFocused =
            dom.contains(activeElement) ||
            dom === activeElement ||
            !!activeElement?.closest(`[${SUGGESTIONS_MENU_ATTR}]`);
        }
      });

      if (typeof window !== "undefined") {
        // On focusout, activeElement is briefly <body> before moving to the
        // next element, so defer to a microtask to read the settled focus.
        // focusin needs no defer — activeElement is already correct there.
        const handleFocusOut = () => queueMicrotask(handleFocusChange);
        document.addEventListener("focusin", handleFocusChange);
        document.addEventListener("focusout", handleFocusOut);
        this.disposeHandleFocusChange = () => {
          document.removeEventListener("focusin", handleFocusChange);
          document.removeEventListener("focusout", handleFocusOut);
        };
      }

      handleFocusChange();

      this.nodeLookup = new NodeLookupModel(nodesJson as FlattenedNodeJson[]);
    }

    if (process.env.NODE_ENV === "development") {
      this.disposeSpy = spy((e) => {
        if (e.type === "report-end") {
          this.spyStack.pop();
        } else if (e.spyReportStart) {
          this.spyStack.push(e.type);
        }
      });
    }
  }

  get isInTransaction() {
    return !!this.currentTransactionName;
  }

  get cursorContextId(): string {
    return this._cursorContextId;
  }

  set cursorContextId(value: string | undefined) {
    this._cursorContextId = value ?? DEFAULT_CURSOR_CONTEXT_ID;
  }

  get cursor(): CursorModel {
    return this.cursorMap.cursor(this.cursorContextId);
  }

  get selection(): SelectionModel {
    return this.cursorMap.selection(this.cursorContextId);
  }

  /**
   * Returns the cursor context for the current document.
   * If `contextId` is provided, it returns the cursor context for that specific context.
   * If no contextId is provided, it returns the default cursor context (`DEFAULT_CURSOR_CONTEXT_ID`)
   * which is initialized in `DocumentModel` constructor.
   */
  cursorContext(): CursorContext;

  cursorContext(contextId?: string): CursorContext | undefined;

  cursorContext(contextId?: string): CursorContext | undefined {
    if (!contextId) {
      return this.cursorMap.cursors.get(DEFAULT_CURSOR_CONTEXT_ID)!;
    }

    return this.cursorMap.cursors.get(contextId);
  }

  dispose() {
    this.disposeSpy?.();
    this.disposeHandleFocusChange?.();
    this.computedNodes?.dispose();
    this.cursorMap.dispose();
    this.placeholderRegistry.dispose();
    this.nodeLookup?.dispose();
    [...this.nodesReactions.values()].forEach((r) => r());
    this.nodesReactions.clear();
    this.unsubscribe();
    this.marimoManager?.dispose();
    this.editor.dispose();
  }

  subscribe() {
    if (this.id) {
      this.cableChannel = subscribe("DocumentChannel", { document_id: this.id });
    }
  }

  unsubscribe() {
    if (this.cableChannel) {
      this.cableChannel.disconnect();
    }
  }

  get unreadMessagesCount() {
    return this.api.unreadMessagesCount;
  }

  get editable(): boolean {
    if (this.disableEditing) {
      return false;
    }
    if (this.api.locked) {
      return false;
    }
    if (this.yProvider) {
      return this.yProvider.authorizedScope === "read-write";
    }
    return this.permissions.write;
  }

  get publicRead(): boolean {
    return this.api.publicRead;
  }

  get publicWrite(): boolean {
    return this.api.publicWrite;
  }

  get locked(): boolean {
    return this.api.locked;
  }

  get team() {
    return this.api.team;
  }

  get title() {
    return this.api.title;
  }

  create<
    T extends NodeModel,
    TWithConstructor extends (new (...args: unknown[]) => T) & T,
    Args extends ConstructorParameters<TWithConstructor>,
  >(
    NodeClass: {
      create(doc: DocumentModel, ...args: Args): T;
    },
    ...args: Args
  ): T {
    const node = NodeClass.create(this, ...args);
    this.registerNode(node);
    return node;
  }

  private runTransactCallback<T>(cb: () => T, options: TransactionOptions = {}): T {
    if (options.runInAction) {
      return runInAction(() => cb());
    }
    return cb();
  }

  /**
   * All changes in the document should run in transaction to have a proper sync to yjs,
   * set up cleanup, updates and reactions.
   * Note: if you use runInAction along with doc.transact, runInAction should be inside the
   * doc.transact to make all mobx reactions work correctly. You can pass `{ runInAction: true }`
   * option to automatically wrap transaction with mobx runInAction.
   */
  transact<T>(cb: () => T, transactionName: string, options: TransactionOptions = {}): T {
    if (this.currentTransactionName) {
      logger.debug(
        "Already in transaction:",
        this.currentTransactionName,
        ", omit transaction start:",
        transactionName,
      );

      return this.runTransactCallback(cb, options);
    }

    // Nodes need to be cleaned up, and nested transactions have to be recognized as such.
    if (!this.yDoc) {
      this.currentTransactionName = transactionName;
      try {
        const result = this.runTransactCallback(cb, options);
        this.finalizeTransaction(options);
        return result;
      } finally {
        this.currentTransactionName = null;
      }
    }

    if (this.spyStack.some((t) => t === "action")) {
      /**
       * doc.transact() should always wrap any action() calls, otherwise it may lead to incosistent app state.
       *
       * @example This is incorrect:
       * ```ts
       * const handleClick = action(() => {
       *   doc.transact(() => {
       *     node.value = "x";
       *   }, "transaction name");
       * });
       * ```
       *
       * @example This is better:
       * ```ts
       * const handleClick = () => {
       *   doc.transact(() => {
       *     runInAction(() => {
       *       node.value = "x";
       *     });
       *   }, "transaction name");
       * };
       * // or
       * const handleClick = () => {
       *   doc.transact(() => {
       *     node.value = "x";
       *   }, "transaction name", { runInAction: true });
       * };
       * ```
       */
      logger.warn(
        `Trying to run doc.transact() inside of MobX action(). See the comment in DocumentModel for more info`,
      );
    }

    this.currentTransactionName = transactionName;
    let hadNodeChanges = false;
    const result = this.yDoc.transact((tr) => {
      this.currentTransaction = tr;

      if (options.preventNewUndoStack) {
        tr.meta.set("preventNewUndoStack", true);
      }

      if (options.forceNewUndoStack) {
        tr.meta.set("forceNewUndoStack", true);
      }

      if (options.skipHistory) {
        tr.meta.set("addToHistory", false);
      }

      logger.debug("Start transaction:", transactionName);
      const res = this.runTransactCallback(cb, options);
      logger.debug("Nodes to update", [...this.nodesToUpdate]);
      hadNodeChanges = this.nodesToUpdate.size > 0 || this.nodesToCleanup.size > 0;
      const syncedNodes = this.finalizeTransaction(options);
      logger.debug("End transaction:", transactionName, "nodes synced:", syncedNodes);

      if (tr.meta.get("addToHistory") === false) {
        logger.warn(
          "Transaction with addToHistory=false, it won't be added to undo stack:",
          transactionName,
        );
      }
      return res;
    }, this);

    this.currentTransactionName = null;
    this.currentTransaction = null;

    // Notify the computed nodes manager about node changes, except for changes
    // it produced itself (recalculation results, validity flags, etc.)
    if (hadNodeChanges && !options.skipComputedRecalc && this.api && this.editable) {
      this.computedNodes?.scheduleRecalculation();
    }

    logOrphanNodes(this);

    return result;
  }

  private syncNodeFromYDoc(key: NodeId) {
    const yNodeMap = this.yNodesMap.get(key);
    const oldNode = this.nodes.get(key);

    if (yNodeMap) {
      const nodeValue = serializeYNodeMap(yNodeMap);
      this.nodeLookup?.updateNodeWithAncestors(nodeValue.id, nodeValue as FlattenedNodeJson);

      if (!oldNode) {
        const newNode = nodeFromJson(this, nodeValue);
        if (newNode) {
          this.registerNode(newNode);
        }
      } else if (!isEqual(nodeValue, oldNode.serialized)) {
        if (nodeValue.id !== oldNode.id) {
          throw new Error(`Can't change node id ${nodeValue.id} ${oldNode.id}`);
        }
        if (nodeValue.type !== oldNode.type) {
          throw new Error(`Can't change node type ${nodeValue.type} ${oldNode.type}`);
        }
        (oldNode as BaseNodeModel<NodeProps>).setProps(nodeValue.props);
        oldNode.setChildren(nodeValue.childrenIds, true);
      }
    } else if (oldNode) {
      this.unregisterNode(oldNode);
    }
  }

  /**
   * Performance optimization for creating nested nodes.
   * You can wrap the node creation code with this function to reduce multiple
   * recursive calls to `registerNode` and apply other performance optimizations.
   * The supplied function should return the root node that was created.
   * It is recommended to wrap all node creations, including small temporary nodes
   * used for previews in suggestions and the right sidebar, as this will
   * significantly improve rendering performance.
   */
  batchCreateNodes<T extends ExpressionNodeModel | ExpressionNodeModel[] | undefined | null>(
    cb: () => T,
  ) {
    if (this.isBatchCreateNodes) {
      const rootNode = cb();
      if (rootNode) {
        if (Array.isArray(rootNode)) {
          for (const node of rootNode) {
            this.registerNode(node as ExpressionNodeModel);
          }
        } else {
          this.registerNode(rootNode);
        }
      }
      return rootNode;
    }

    this.isBatchCreateNodes = true;
    const rootNode = cb();
    this.isBatchCreateNodes = false;
    if (rootNode) {
      if (Array.isArray(rootNode)) {
        for (const node of rootNode) {
          this.registerNode(node as ExpressionNodeModel);
        }
      } else {
        this.registerNode(rootNode);
      }
    }
    return rootNode;
  }

  copyDataFromYDoc() {
    const nodesJson: SerializedNode[] = [];

    this.isInitialLoad = true;
    try {
      this.batchCreateNodes(() => {
        this.yNodesMap.forEach((yNodeMap) => {
          const nodeData = serializeYNodeMap(yNodeMap);
          nodesJson.push(nodeData);

          const newNode = nodeFromJson(this, nodeData);
          if (newNode) {
            this.registerNode(newNode);
          }
        });

        const textNode = this.nodes.get("textNode");
        if (textNode?.is(TextNodeModel)) {
          this.textNode = textNode;
        }
        return textNode;
      });
    } finally {
      this.isInitialLoad = false;
    }

    return nodesJson;
  }

  private observeYDoc() {
    this.yNodesMap.observeDeep((events, transaction) => {
      if (transaction.origin === this) {
        // no need to sync changes that were made by this instance
        return;
      }

      logger.debug("yNodesMap updated", events);
      this.transact(
        () => {
          runInAction(() => {
            const keysChanged = events.reduce((r, e) => {
              if (e.path[0]) {
                r.add(String(e.path[0]));
              } else {
                [...e.changes.keys.keys()].forEach((key) => r.add(key));
              }
              return r;
            }, new Set<string>());
            for (const key of keysChanged) {
              this.syncNodeFromYDoc(key);
            }
          });
        },
        "DocumentModel:observeNodesMap",
        { skipYDocUpdate: true },
      );
    });
  }

  registerNode(node: ExpressionNodeModel) {
    this.nodes.set(node.id, node);
    this.nodesToCleanup.delete(node.id);

    this.addNodeByType(node);

    if (this.isBatchCreateNodes) {
      return;
    }

    if (this.yDoc && !this.currentTransactionName) {
      logger.warn("registerNode should be run in transaction");
      Sentry.captureMessage("registerNode should be run in transaction");
    }

    if (!node.parent) {
      for (const n of this.nodes.values()) {
        if (n.childrenIds?.includes(node.id)) {
          node.parent = n;
          break;
        }
      }
    }

    this.nodesToUpdate.add(node.id);

    // Detect that nodes are changed outside of a transaction
    if (this.yDoc && !this.nodesReactions.has(node.id)) {
      this.nodesReactions.set(
        node.id,
        reaction(
          () => node.props,
          () => {
            this.nodesToUpdate.add(node.id);
            if (!this.currentTransactionName) {
              logger.warn("Update node should be run within a transaction.");
              Sentry.captureMessage("Update node should be run within a transaction.");
            }
          },
        ),
      );
    }

    for (const id of node.childrenIds) {
      const childNode = this.nodes.get(id);
      if (childNode) {
        childNode.parent = node;
        this.registerNode(childNode);
      }
    }
  }

  unregisterNode(node: ExpressionNodeModel) {
    if (this.nodes.has(node.id)) {
      if (this.yDoc && !this.currentTransactionName) {
        logger.warn("unregisterNode should be run in transaction");
        Sentry.captureMessage("unregisterNode should be run in transaction");
      }

      this.nodesToCleanup.set(node.id, node);
      node.childrenPlease.forEach((childNode) => this.unregisterNode(childNode));
    }
  }

  private cleanupNodes(options: TransactionOptions) {
    runInAction(() => {
      const nodesToDelete: ExpressionNodeModel[] = [];
      this.nodesToCleanup.forEach((node) => {
        if (this.nodes.has(node.id)) {
          nodesToDelete.push(node);
        }
      });

      if (nodesToDelete.length > 0) {
        if (this.yDoc && !this.currentTransactionName) {
          logger.warn("cleanupNodes should be run in transaction");
          Sentry.captureMessage("cleanupNodes should be run in transaction");
        }

        for (const node of nodesToDelete) {
          this.nodeLookup?.deleteNode(node.id);

          node.cleanup();
          this.nodes.delete(node.id);
          this.removeNodeByType(node);
          if (!options?.skipYDocUpdate && this.yDoc) {
            if (this.yNodesMap?.has(node.id)) {
              this.yNodesMap.delete(node.id);
            }
            if (node.is(WrapperNodeModel)) {
              this.cleanParentTiptapNode(node);
            }
          }
          const nodeReaction = this.nodesReactions.get(node.id);
          if (nodeReaction) {
            nodeReaction();
            this.nodesReactions.delete(node.id);
          }
        }
      }

      this.nodesToCleanup.clear();
    });
  }

  private updateNodes(options: TransactionOptions) {
    if (options?.skipYDocUpdate || !this.yDoc) {
      this.nodesToUpdate.clear();
      this.nodesToTransform.clear();
      return;
    }

    for (const id of this.nodesToUpdate) {
      const node = this.nodes.get(id);
      let yNodeMap = this.yNodesMap.get(id);

      if (node) {
        const { serialized } = node;
        const { type, props, childrenIds } = serialized;

        // do we need this?
        this.nodeLookup?.updateNodeWithAncestors(serialized.id, serialized as FlattenedNodeJson);

        if (!yNodeMap || !("set" in yNodeMap)) {
          yNodeMap = new Y.Map();
          yNodeMap.set("id", id);
          yNodeMap.set("type", type);
          this.yNodesMap.set(id, yNodeMap);
        }

        let yPropsMap = yNodeMap.get("props") as YNode["props"];

        if (!yPropsMap) {
          yPropsMap = new Y.Map<NodePropsValue>();
          yNodeMap.set("props", yPropsMap);
        }
        Object.entries(props).forEach(([key, value]) => {
          if (!yPropsMap.has(key) || !isEqual(yPropsMap.get(key), value)) {
            yPropsMap.set(key, value);
          }
        });

        // Syncronize childrenIds
        let childrenArray = yNodeMap.get("childrenIds") as YNode["childrenIds"];
        // Create a new array if it is empty
        if (!childrenArray) {
          childrenArray = new Y.Array();
          childrenArray.insert(0, childrenIds);
          yNodeMap.set("childrenIds", childrenArray);
        }
        // Sync arrays using insert and delete methods from yjs
        else if (!isEqual([...childrenArray], childrenIds)) {
          // Delete removed elements from the childrenArray
          for (let i = childrenArray.length - 1; i >= 0; i--) {
            const childId = childrenArray.get(i);
            if (!childrenIds.includes(childId)) {
              childrenArray.delete(i, 1);
            }
          }

          let currentChildren = [...childrenArray];
          // Iterate the target array to check sync it with the old one
          childrenIds.forEach((childId, index) => {
            const currentIndex = currentChildren.indexOf(childId);
            if (currentIndex === -1) {
              // Insert missing elements
              childrenArray.insert(index, [childId]);
              currentChildren = [...childrenArray];
            } else if (currentIndex !== index) {
              // Reorder existing elements if they are not in the right place
              childrenArray.delete(currentIndex, 1);
              childrenArray.insert(index, [childId]);
              currentChildren = [...childrenArray];
            }
          });
        }
      }
    }
    for (const id of this.nodesToTransform) {
      const node = this.nodes.get(id);
      if (node && "needsTransform" in node) node.needsTransform = true;
    }

    this.nodesToUpdate.clear();
    this.nodesToTransform.clear();
  }

  private finalizeTransaction(options: TransactionOptions) {
    this.maintainComputedStructure(options);
    this.updateNodes(options);
    this.cleanupNodes(options);
  }

  private maintainComputedStructure(options: TransactionOptions) {
    if (this.nodesToUpdate.size === 0 && this.nodesToCleanup.size === 0) {
      return;
    }
    if (options.skipComputedRecalc || options.skipYDocUpdate || !this.api) {
      return;
    }
    if (!this.editable) {
      return;
    }
    this.computedNodes?.maintainStructure();
  }

  cleanParentTiptapNode(node: WrapperNodeModel) {
    const { tiptap } = this.editor;
    if (!tiptap) {
      return;
    }
    const { state } = tiptap;
    state.doc.descendants((n, position) => {
      if (nodeIsExpression(n) && n.attrs.nodeId === node.id) {
        if (position === -1) {
          return;
        }

        tiptap
          .chain()
          .focus()
          .deleteRange({ from: position, to: position + 1 })
          .run();
      }
      this.cursor.reset();
    });
  }

  removeNode(node: ExpressionNodeModel) {
    if (this.yDoc && !this.currentTransactionName) {
      logger.warn("removeNode should be run in transaction");
      Sentry.captureMessage("removeNode should be run in transaction");
    }
    node.removeFromParent();
    this.unregisterNode(node);
  }

  addNodeByType(node: ExpressionNodeModel): void {
    if (!this.nodesByType[node.type]) {
      // @ts-expect-error ...
      this.nodesByType[node.type] = new Map();
    }
    // @ts-expect-error ...
    this.nodesByType[node.type]!.set(node.id, node);
  }

  removeNodeByType(node: ExpressionNodeModel): void {
    if (this.nodesByType[node.type]) {
      this.nodesByType[node.type]?.delete(node.id);
    }
  }

  getNodesByType<T extends NodeModel["type"]>(type: T): NodeModel<T>[] {
    const typeMap = this.nodesByType[type] as Map<NodeId, NodeModel<T>>;
    if (typeMap) {
      return [...typeMap.values()];
    }
    return [];
  }

  // Driven by CodeBlockLineWrapper's mount effect. Accurate only because codeBlock
  // NodeViews aren't virtualized — if they ever adopt TiptapInlineExpression's
  // lazy/eager pattern, switch to an extension-level signal.
  trackCodeBlockMount(): () => void {
    runInAction(() => {
      this.codeBlockCount += 1;
    });
    return () => {
      runInAction(() => {
        this.codeBlockCount -= 1;
      });
    };
  }

  get hasCodeBlock(): boolean {
    return this.codeBlockCount > 0;
  }

  set idGenerator(newIdGenerator: () => string) {
    this._idGenerator = newIdGenerator;
  }

  generateNodeId(): string {
    let id = this._idGenerator();
    while (this.yDoc && this.yNodesMap.has(id)) {
      id = this._idGenerator();
    }
    return id;
  }

  toJSON() {
    const { textNode } = this;
    return { textNode };
  }

  get serialized() {
    const nodes: Record<NodeId, SerializedNode> = {};

    this.nodes.forEach((node) => {
      nodes[node.id] = node.serialized;
    });

    return {
      nodes,
    };
  }

  focus() {
    if (this.cursor.isActive && this.editor.editorState.isExpression) {
      this.input.focus();
    } else {
      this.editor.tiptap?.commands.focus();
    }
  }

  clear() {
    this.nodes.clear();
    this.input.reset();
    this.cursor.reset();
    this.editor.tiptap?.chain().clearContent().focus().run();
    this.nodesByType = {};
  }

  private marimoManager: MarimoManager | null = null;

  get marimo() {
    if (!this.marimoManager) this.marimoManager = new MarimoManager(this);

    return this.marimoManager;
  }
}
